import React, { useState, useEffect, useMemo } from 'react';
import { Box, TextField, Avatar, Typography, IconButton } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import { useKBTheme } from '../../hooks/useKBTheme';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  ticketCreated?: boolean;
}

export const AnimatedChatDemo: React.FC = () => {
  const { mode } = useKBTheme();
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [showTyping, setShowTyping] = useState(false);
  const [inputText, setInputText] = useState('');

  const conversationScript: Message[] = useMemo(() => [
    { id: '1', role: 'user' as const, content: 'I need Adobe Photoshop installed on my laptop' },
    { id: '2', role: 'assistant' as const, content: "I'll create a support ticket for your Adobe Photoshop installation request. Our IT team will contact you within 1-2 business days.", ticketCreated: true },
    { id: '3', role: 'user' as const, content: 'What is our vacation policy?' },
    { id: '4', role: 'assistant' as const, content: 'According to our company policy, employees accrue 15 days of vacation per year. Vacation requests should be submitted at least 2 weeks in advance through the HR portal.' },
  ], []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (currentMessageIndex < conversationScript.length) {
        const message = conversationScript[currentMessageIndex];
        
        if (message.role === 'user') {
          // Simulate typing user message
          setShowTyping(true);
          setTimeout(() => {
            setMessages((prev) => [...prev, message]);
            setShowTyping(false);
            setCurrentMessageIndex((prev) => prev + 1);
          }, 500);
        } else {
          // Simulate AI response with delay
          setShowTyping(true);
          setTimeout(() => {
            setMessages((prev) => [...prev, message]);
            setShowTyping(false);
            setCurrentMessageIndex((prev) => prev + 1);
          }, 1500);
        }
      } else {
        // Reset after showing all messages
        setTimeout(() => {
          setMessages([]);
          setCurrentMessageIndex(0);
        }, 3000);
      }
    }, currentMessageIndex === 0 ? 1000 : 2500);

    return () => clearInterval(interval);
  }, [currentMessageIndex, conversationScript]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', background: mode === 'dark' ? '#1a1a1a' : '#ffffff' }}>
      {/* Messages Area */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {messages.map((msg, idx) => (
          <Box
            key={idx}
            sx={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              gap: 1.5,
              animation: 'messageSlideIn 0.3s ease-out',
              '@keyframes messageSlideIn': {
                '0%': { opacity: 0, transform: 'translateY(10px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            {msg.role === 'assistant' && (
              <Avatar sx={{ bgcolor: '#6366f1', width: 32, height: 32, flexShrink: 0 }}>
                <SmartToyIcon sx={{ fontSize: 18 }} />
              </Avatar>
            )}
            <Box
              sx={{
                maxWidth: '75%',
                background: msg.role === 'user'
                  ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                  : mode === 'dark'
                  ? '#2f2f2f'
                  : '#f7f7f8',
                color: msg.role === 'user' ? '#fff' : mode === 'dark' ? '#fff' : '#000',
                px: 3,
                py: 2.5,
                borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                fontSize: '0.9375rem',
                lineHeight: 1.6,
              }}
            >
              <Typography variant="body2" sx={{ color: 'inherit' }}>
                {msg.content}
              </Typography>
              {msg.ticketCreated && (
                <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(99, 102, 241, 0.2)', border: '1.5px solid #6366f1' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#6366f1', display: 'block', mb: 0.5 }}>
                    ✓ TICKET T-1234 CREATED
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.8 }}>
                    Software Installation Request • Assigned to IT Team
                  </Typography>
                </Box>
              )}
            </Box>
            {msg.role === 'user' && (
              <Avatar sx={{ bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(2,6,23,0.06)', width: 32, height: 32, flexShrink: 0 }}>
                <PersonIcon sx={{ fontSize: 18 }} />
              </Avatar>
            )}
          </Box>
        ))}
        {showTyping && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', animation: 'fadeIn 0.3s' }}>
            <Avatar sx={{ bgcolor: '#6366f1', width: 32, height: 32 }}>
              <SmartToyIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Box sx={{ display: 'flex', gap: 0.5, px: 3, py: 2.5, borderRadius: '18px 18px 18px 4px', bgcolor: mode === 'dark' ? '#2f2f2f' : '#f7f7f8' }}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.4)' : '#94a3b8',
                    animation: 'typing 1.4s infinite',
                    animationDelay: `${i * 0.2}s`,
                    '@keyframes typing': {
                      '0%, 60%, 100%': { transform: 'translateY(0)', opacity: 0.4 },
                      '30%': { transform: 'translateY(-10px)', opacity: 1 },
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Input Area */}
      <Box sx={{ p: 2, borderTop: `1px solid ${mode === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(226, 232, 240, 0.8)'}` }}>
        <Box sx={{ display: 'flex', gap: 1, maxWidth: 768, mx: 'auto' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Message CoreSupport..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                background: mode === 'dark' ? 'rgba(47, 47, 47, 0.8)' : 'rgba(255, 255, 255, 0.8)',
                borderRadius: 3,
                border: `1px solid ${mode === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(226, 232, 240, 0.8)'}`,
              },
            }}
          />
          <IconButton
            sx={{
              bgcolor: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              borderRadius: 3,
              minWidth: 48,
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
              },
            }}
          >
            <SendIcon sx={{ color: '#fff' }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

